import { useEffect, useRef, useState, type CSSProperties } from 'react';
import ComplexityHeaderGraphic, { type ComplexityPhase } from './ComplexityHeaderGraphic';

/**
 * Places {@link ComplexityHeaderGraphic} in the lesson header (MUS-401).
 *
 * The graphic's geometry is in absolute pixels — the chevron array is measured
 * back from the right edge and the progress strip has to reach the left one —
 * so it needs its container's real size rather than percentages. This measures
 * that box and re-renders on resize, which the header does on orientation
 * change and when the mobile layout slides during playback.
 *
 * Nothing renders until the first measurement lands, so the figure never flashes
 * at a wrong width.
 */

interface ComplexityHeaderFigureProps {
  variant: 'mobile' | 'desktop';
  /** Placement within the header. The figure fills whatever box this gives it. */
  style: CSSProperties;
  /** Progress toward chevron activation, [0, 1]. */
  fillFraction: number;
  /** Gates the fill's momentum on a sustained climb (MUS-461). */
  streakBoost: boolean;
  chevronsFilled: number;
  pulsing: boolean;
  /** Seconds per pulse cycle, derived from tempo by the caller. */
  pulseSeconds: number;
  accuracyLabel: string | null;
  accuracyColor: string;
  /** Dims the readout while a complexity rest phrase plays. */
  accuracyDimmed: boolean;
  /** Mobile only: drives the paused <-> playing choreography. */
  phase: ComplexityPhase;
}

const ComplexityHeaderFigure = ({
  variant,
  style,
  fillFraction,
  streakBoost,
  chevronsFilled,
  pulsing,
  pulseSeconds,
  accuracyLabel,
  accuracyColor,
  accuracyDimmed,
  phase,
}: ComplexityHeaderFigureProps) => {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);

  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;

    const measure = () => {
      const rect = box.getBoundingClientRect();
      // The header carries a slide transform during mobile playback, which
      // scales nothing but would still report a stale size if measured mid
      // transition; width and height are transform-invariant here because the
      // slide is a pure translate.
      setSize((current) =>
        current?.width === rect.width && current?.height === rect.height
          ? current
          : { width: rect.width, height: rect.height },
      );
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    window.addEventListener('orientationchange', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('orientationchange', measure);
    };
  }, []);

  return (
    <div ref={boxRef} style={{ ...style, pointerEvents: 'none' }}>
      {size && size.width > 0 ? (
        <ComplexityHeaderGraphic
          variant={variant}
          width={size.width}
          height={size.height}
          fillFraction={fillFraction}
          streakBoost={streakBoost}
          chevronsFilled={chevronsFilled}
          pulsing={pulsing}
          pulseSeconds={pulseSeconds}
          accuracyLabel={accuracyLabel}
          accuracyColor={accuracyColor}
          accuracyDimmed={accuracyDimmed}
          phase={phase}
        />
      ) : null}
    </div>
  );
};

export default ComplexityHeaderFigure;
